import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { Printer, Clock, Info, ChevronDown, Edit, Send, Filter, X, ChevronUp, Settings2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/ReportsScreen.css';

import SplitPayrollModal from '../components/payroll/SplitPayrollModal'; 
import SummaryReportModal from '../components/reports/SummaryReportModal'; 
import { generateReportPDF } from '../components/reports/ReportGenerator';
// --- CONSTANTS & HELPERS ---

const calculateDateRange = (filter, customDates) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const toLocalISOString = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    switch (filter) {
        case 'firstHalf': 
            return { startDate: toLocalISOString(new Date(year, month, 1)), endDate: toLocalISOString(new Date(year, month, 15)) };
        case 'secondHalf':
            const lastDay = new Date(year, month + 1, 0);
            return { startDate: toLocalISOString(new Date(year, month, 16)), endDate: toLocalISOString(lastDay) };
        case 'week':
            const day = today.getDay(); // 0 is Sunday
            const diffToMonday = today.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(today);
            monday.setDate(diffToMonday);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return { startDate: toLocalISOString(monday), endDate: toLocalISOString(sunday) };
        case 'today': 
            return { startDate: toLocalISOString(today), endDate: toLocalISOString(today) };
        case 'month':
            const firstDayOfMonth = new Date(year, month, 1);
            const lastDayOfMonth = new Date(year, month + 1, 0);
            return { startDate: toLocalISOString(firstDayOfMonth), endDate: toLocalISOString(lastDayOfMonth) };
        case 'custom':
            if (!customDates.start || !customDates.end) { return { error: 'Incomplete dates' }; }
            return { startDate: customDates.start, endDate: customDates.end };
        default: 
            return { error: 'Invalid date filter selected.' };
    }
};

const calculateOvertimeSummary = (entries, thresholdHours = 8) => {
    if (!entries || entries.length === 0) return {};

    // 1. Group duration by User AND Day
    const userDailyTotals = {}; 
    entries.forEach(entry => {
        const start = new Date(entry.start_time);
        const end = entry.end_time ? new Date(entry.end_time) : new Date();
        const durationMillis = end - start;
        const dateKey = start.toDateString(); 
        const userKey = entry.employee_name;

        if (!userDailyTotals[userKey]) userDailyTotals[userKey] = {};
        if (!userDailyTotals[userKey][dateKey]) userDailyTotals[userKey][dateKey] = 0;
        userDailyTotals[userKey][dateKey] += durationMillis;
    });

    // 2. Calculate OT using the DYNAMIC threshold
    const userOvertimeMap = {};
    const thresholdMillis = thresholdHours * 60 * 60 * 1000;

    Object.keys(userDailyTotals).forEach(user => {
        let totalUserOTMillis = 0;
        const days = userDailyTotals[user];
        Object.values(days).forEach(dailyTotal => {
            if (dailyTotal > thresholdMillis) {
                totalUserOTMillis += (dailyTotal - thresholdMillis);
            }
        });

        if (totalUserOTMillis > 0) {
            const h = Math.floor(totalUserOTMillis / 3600000);
            const m = Math.floor((totalUserOTMillis % 3600000) / 60000);
            userOvertimeMap[user] = `${h}h ${m.toString().padStart(2, '0')}m`;
        }
    });

    return userOvertimeMap;
};

const ReportScreen = ({ user }) => {
    // --- STATE ---
    const [properties, setProperties] = useState([]);
    const [usersForFilter, setUsersForFilter] = useState([]);
    const [timeEntries, setTimeEntries] = useState([]);
    const [summary, setSummary] = useState(null);
    
    // Filter State
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [dateFilter, setDateFilter] = useState('today');
    const [customDate, setCustomDate] = useState({ start: '', end: '' });

    // UI State
    const [isLoading, setIsLoading] = useState(true); 
    const [isGenerating, setIsGenerating] = useState(false); 
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
    const [expandedEntryId, setExpandedEntryId] = useState(null);
    
    // Modals State
    const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    
    const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });
    const [payrollData, setPayrollData] = useState(null);

    // Refs
    const abortControllerRef = useRef(null);
    const hasInitialized = useRef(false);

    // Permissions
    const canEdit = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canViewAll = user && user.role === 'SuperAdmin';
    const canSendToAccountant = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);

    const periodType = ['firstHalf', 'secondHalf'].includes(dateFilter) 
        ? (dateFilter === 'firstHalf' ? 'FIRST_HALF' : 'SECOND_HALF') 
        : 'CUSTOM';
    const isPayrollPeriod = ['firstHalf', 'secondHalf'].includes(dateFilter);

    const generateAcronym = (name) => {
        if (!name || typeof name !== 'string') return '';
        const firstWord = name.split(' ')[0];
        return `(${firstWord.toLowerCase()})`;
    };

    const handleToggleExpand = (entryId) => {
        setExpandedEntryId(prevId => (prevId === entryId ? null : entryId));
    };

    // --- MAIN FETCH FUNCTION ---
    const fetchReportData = async (propId, userId, filterType, customRange, propertiesOverride = null) => {
        const currentProp = propId ?? selectedProperty;
        const currentUser = userId ?? selectedUser;
        const currentFilter = filterType ?? dateFilter;
        const currentCustom = customRange ?? customDate;

        if (currentFilter === 'custom') {
            if (!currentCustom.start || !currentCustom.end) {
                setTimeEntries([]);
                setSummary(null);
                setPayrollData(null);
                return; 
            }
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsGenerating(true);
        
        const dateRange = calculateDateRange(currentFilter, currentCustom);
        if (dateRange.error) { 
            if (dateRange.error !== 'Incomplete dates') toast.error(dateRange.error); 
            setIsGenerating(false); 
            return; 
        }
        
        setReportDateRange({ start: dateRange.startDate, end: dateRange.endDate });

        const filters = { 
            propertyId: currentProp || null, 
            employeeId: currentUser || null, 
            startDate: dateRange.startDate, 
            endDate: dateRange.endDate 
        };

        try {
            const { punches: entries, summary: summaryData } = await api.getWorkSummary(filters, { signal });
            
            if (signal.aborted) return; 

            // Find current property object to get settings
            // This is still needed to DISPLAY the overtime correctly
            // Use the override if provided (during init), otherwise use the state
            const effectiveProperties = propertiesOverride || properties;
            
            // Find current property object to get settings
            const propObj = effectiveProperties.find(p => p.id === parseInt(currentProp));
            const isOtEnabled = propObj && (
                propObj.settings_overtime_enabled === 1 || 
                propObj.settings_overtime_enabled === true
            );

            const otThreshold = propObj ? (propObj.settings_overtime_threshold || 8) : 8;

            let enrichedSummary = { ...summaryData };

            // Only calculate if enabled for this property
            if (isOtEnabled) {
                const otMap = calculateOvertimeSummary(entries, otThreshold);
                
                if (enrichedSummary) {
                    Object.keys(enrichedSummary).forEach(employeeName => {
                        if (otMap[employeeName]) {
                            enrichedSummary[employeeName].overtime_hours = otMap[employeeName];
                        }
                    });
                }
            }

            setTimeEntries(entries);
            setSummary(enrichedSummary);
            setExpandedEntryId(null);
            setPayrollData(null);

            if (currentProp && ['firstHalf', 'secondHalf'].includes(currentFilter)) {
                try {
                    const statement = await api.getPaymentStatement({
                        propertyId: currentProp,
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate,
                        periodType: currentFilter === 'firstHalf' ? 'FIRST_HALF' : 'SECOND_HALF'
                    });
                    if (!signal.aborted) setPayrollData(statement);
                } catch (draftError) {
                    if (!signal.aborted) setPayrollData(null);
                }
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                toast.error(err.message);
            }
        } finally {
            if (!signal.aborted) {
                setIsGenerating(false);
            }
        }
    };
    
    // --- AUTO-FETCH EFFECT ---
    useEffect(() => {
        if (isLoading || !hasInitialized.current) return;
        fetchReportData();
    }, [selectedProperty, selectedUser, dateFilter, customDate]);


    // --- INITIALIZATION EFFECT ---
    useEffect(() => {
        const initializeScreen = async () => {
            setIsLoading(true);
            try {
                const [props, preferences] = await Promise.all([
                    api.getProperties(),
                    api.getUserPreferences()
                ]);

                setProperties(props);

                const getPref = (key, fallback) => {
                    const p = preferences.find(x => x.preference_key === key);
                    return p ? p.preference_value : fallback;
                };

                let initialPropId = '';
                const savedPropId = getPref('default_property_id', '');
                
                if (savedPropId && props.some(p => p.id.toString() === savedPropId.toString())) {
                    initialPropId = savedPropId;
                } else if (props.length > 0) {
                    initialPropId = (props.find(p => p.is_active) || props[0]).id;
                }
                setSelectedProperty(initialPropId);

                let initialDateFilter = 'today';
                const savedView = getPref('default_report_view', 'today');

                if (savedView === 'twice_a_month') {
                    const dayOfMonth = new Date().getDate();
                    initialDateFilter = dayOfMonth <= 15 ? 'firstHalf' : 'secondHalf';
                } else if (savedView === 'month') {
                    initialDateFilter = 'month';
                }
                setDateFilter(initialDateFilter);
                
                hasInitialized.current = true;
                await fetchReportData(initialPropId, '', initialDateFilter, { start: '', end: '' }, props);

            } catch (err) {
                console.error("Init Error:", err);
                try {
                    const fallbackProps = await api.getProperties();
                    setProperties(fallbackProps);
                } catch(e) {}
            } finally {
                setIsLoading(false);
            }
        };

        if (user) {
            initializeScreen();
        }
    }, [user]); 

    // --- LOAD USERS EFFECT ---
    useEffect(() => {
        setSelectedUser('');
        const fetchUsersForProperty = async () => {
            if (!selectedProperty) {
                setUsersForFilter([]);
                return;
            }
            try {
                const filters = { propertyId: selectedProperty };
                const users = await api.getWorkforce(filters);
                const housekeepers = users.filter(u => u.role === 'Housekeeper');
                setUsersForFilter(housekeepers);
            } catch (err) {
                console.error("Error loading users", err);
                setUsersForFilter([]);
            }
        };
        fetchUsersForProperty();
    }, [selectedProperty]); 

    // --- ACTIONS ---
    const handleSendReport = async () => {
        if (!selectedProperty) { toast.error('Please select a specific property to send the report.'); return; }
        const currentProperty = properties.find(p => p.id === parseInt(selectedProperty));
        
        // Pass summary (which includes Overtime) to generator
        const doc = generateReportPDF('COMBINED', {
            summary, timeEntries, currentProperty, dateFilter, customDate, payrollData, user
        });

        if (doc) {
            const pdfData = doc.output('datauristring');
            const toastId = toast.loading('Sending report...');
            api.sendTimeEntries({ propertyId: selectedProperty, propertyName: currentProperty?.name, pdfData: pdfData })
            .then((res) => {
                toast.dismiss(toastId);
                toast.success(res.message);
            })
            .catch((err) => {
                toast.dismiss(toastId);
                toast.error(err.message, { duration: 6000 });
            });
        }
    };

    const handleOpenEditModal = (entry) => { setEditingEntry(entry); setIsEditModalOpen(true); };
    const handleCloseEditModal = () => { setIsEditModalOpen(false); setEditingEntry(null); };
        // --- NEW: Real-time validation for the Edit Modal ---
    const isEditFormValid = useMemo(() => {
        if (!editingEntry) return false;

        // 1. Notes are required
        if (!editingEntry.notes || editingEntry.notes.trim().length === 0) return false;

        // 2. End Time is required
        if (!editingEntry.end_time) return false;

        // 3. Logic: End must be strictly AFTER Start
        const start = new Date(editingEntry.start_time);
        const end = new Date(editingEntry.end_time);
        
        return end > start;
    }, [editingEntry]);
    const handleSaveEntry = async () => {
        if (!editingEntry.notes) { toast.error('Notes are required.'); return; }
        
        // Convert to Date objects for comparison
        const startTime = new Date(editingEntry.start_time);
        const endTime = new Date(editingEntry.end_time);

        // 1. Check if End is before Start
        if (endTime <= startTime) { 
            toast.error('End time cannot be earlier than or equal to Start time.'); 
            return; 
        }

        // 2. Check for Max Duration (12 Hours)
        const diffInMillis = endTime - startTime;
        const diffInHours = diffInMillis / (1000 * 60 * 60);

        if (diffInHours > 12) {
            toast.error(`Shift cannot exceed 12 hours (You entered ${diffInHours.toFixed(2)}h). Please check AM/PM or the Date.`);
            return;
        }

        try {
            await api.updateTimeEntry(editingEntry.id, editingEntry);
            toast.success('EndTime Updated Successfully!');
            handleCloseEditModal();
            fetchReportData(); 
        } catch (err) {
            toast.error(err.message);
        }
    };
    
    
    // --- NEW: Helper to format ISO string to Input values ---
    const getInputValue = (isoString, type) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (type === 'date') {
            // Return YYYY-MM-DD
            return date.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format consistently
        }
        if (type === 'time') {
            // Return HH:mm (24h format for input)
            return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        }
        return '';
    };

    // --- NEW: Handle Date OR Time changes ---
    const handleDateTimeChange = (field, type, value) => {
        if (!value) return; // specific part cleared, ignore or handle if needed

        const currentIso = editingEntry[field] || new Date().toISOString();
        const dateObj = new Date(currentIso);

        if (type === 'date') {
            // Value is YYYY-MM-DD
            const [y, m, d] = value.split('-').map(Number);
            dateObj.setFullYear(y, m - 1, d);
        } else if (type === 'time') {
            // Value is HH:mm
            const [h, m] = value.split(':').map(Number);
            dateObj.setHours(h, m);
        }

        setEditingEntry({ ...editingEntry, [field]: dateObj.toISOString() });
    };

    const handleTimeChange = (field, newTime) => {
        if (!newTime) { setEditingEntry({...editingEntry, [field]: null }); return; }
        const originalDateTime = new Date(editingEntry.start_time);
        const newDateTime = new Date(originalDateTime.toDateString() + ' ' + newTime);
        setEditingEntry({...editingEntry, [field]: newDateTime.toISOString() });
    };

    const getFilterSummaryText = () => {
        const propName = selectedProperty ? properties.find(p => p.id === parseInt(selectedProperty))?.name : 'All Properties';
        const userName = selectedUser ? usersForFilter.find(u => u.id === parseInt(selectedUser))?.first_name : 'All Users';
        return `${propName} • ${userName} • ${dateFilter}`;
    };

    const totalWorkHours = useMemo(() => {
        if (!summary) return "0h 0m";
        const totalMinutes = Object.values(summary).reduce((acc, employeeSummary) => {
            const timeString = employeeSummary.total_hours || "0h 0m";
            const hourMatch = timeString.match(/(\d+)\s*h/);
            const minuteMatch = timeString.match(/(\d+)\s*m/);
            return acc + ((hourMatch ? parseInt(hourMatch[1], 10) : 0) * 60) + (minuteMatch ? parseInt(minuteMatch[1], 10) : 0);
        }, 0);
        return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
    }, [summary]);

    if (isLoading) { return <div className="loading-screen"><Loader2 className="spin" size={48} /></div> }



    return (
        <div className="reports-page-container">
            <div className="page-header no-print">
                <h1 className="page-title">Reports</h1>
                <div className="page-header-actions">
                    <button onClick={() => setIsSummaryModalOpen(true)} className="icon-action-button" title="Generate PDF" disabled={!summary || Object.keys(summary).length === 0}><Printer size={18} /></button>
                    {canSendToAccountant && (<button onClick={handleSendReport} className="icon-action-button" title="Send Report" disabled={!summary || Object.keys(summary).length === 0 || !selectedProperty}><Send size={18} /></button>)}                
                    
                    {/* FIXED: Removed Settings Button from here */}
                    
                    <button 
                        onClick={() => setIsPayrollModalOpen(true)} 
                        className="icon-action-button" 
                        title="Split Payroll"
                        disabled={!summary || Object.keys(summary).length === 0 || !selectedProperty || !isPayrollPeriod} 
                    >
                        <Settings2 size={18} />
                    </button>         
                </div>
            </div>

            {/* Mobile Filter Toggle */}
            <div className="filter-bar mobile-only" onClick={() => setIsFilterSheetOpen(true)}>
                <Filter size={16} />
                <span>{getFilterSummaryText()}</span>
                <ChevronDown size={20} />
            </div>

            {/* Desktop Filters */}
            <div className="card no-print filter-card desktop-only">
                <div className="report-filters">
                    <div className="filter-group">
                        <label>Property</label>
                        <select className="form-input" value={selectedProperty} onChange={(e) => { setSelectedProperty(e.target.value); }}>
                            {canViewAll && <option value="">All Properties</option>}
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>User</label>
                        <select 
                            className="form-input" 
                            value={selectedUser} 
                            onChange={e => setSelectedUser(e.target.value)}
                            disabled={!selectedProperty}
                            style={{ cursor: !selectedProperty ? 'not-allowed' : 'pointer', opacity: !selectedProperty ? 0.6 : 1 }}
                        >
                            <option value="">{selectedProperty ? "All Users" : "Select Property First"}</option>
                            {usersForFilter.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                        </select>
                    </div>
                    <div className="filter-group date-range-group">
                        <div className="date-filter-options">
                            <button className={dateFilter === 'firstHalf' ? 'active' : ''} onClick={() => setDateFilter('firstHalf')}>1st-15th</button>
                            <button className={dateFilter === 'secondHalf' ? 'active' : ''} onClick={() => setDateFilter('secondHalf')}>16th-End</button>
                            <button className={dateFilter === 'today' ? 'active' : ''} onClick={() => setDateFilter('today')}>Today</button>
                            <button className={dateFilter === 'month' ? 'active' : ''} onClick={() => setDateFilter('month')}>This Month</button>
                            <button className={dateFilter === 'custom' ? 'active' : ''} onClick={() => setDateFilter('custom')}>Custom</button>
                        </div>
                        {dateFilter === 'custom' && (
                            <div className="custom-date-inputs">
                                <input type="date" value={customDate.start} onChange={e => setCustomDate({...customDate, start: e.target.value})} className="form-input"/>
                                <span>to</span>
                                <input type="date" value={customDate.end} min={customDate.start} onChange={e => setCustomDate({...customDate, end: e.target.value})} className="form-input"/>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filter Sheet */}
            {isFilterSheetOpen && (
                <div className="filter-sheet-overlay mobile-only" onClick={() => setIsFilterSheetOpen(false)}>
                    <div className="filter-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="filter-sheet-header">
                            <h3>Filters</h3>
                            <button onClick={() => setIsFilterSheetOpen(false)} className="icon-button">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="report-filters">
                            <div className="filter-group">
                                <label>Property</label>
                                <select className="form-input" value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
                                    {canViewAll && <option value="">All Properties</option>}
                                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>User</label>
                                <select 
                                    className="form-input" 
                                    value={selectedUser} 
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    disabled={!selectedProperty}
                                >
                                    <option value="">{selectedProperty ? "All Users" : "Select Property First"}</option>
                                    {usersForFilter.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                                </select>
                            </div>
                            <div className="filter-group date-range-group">
                                <label>Date Range</label>
                                <div className="date-filter-options">
                                    <button className={dateFilter === 'firstHalf' ? 'active' : ''} onClick={() => { setDateFilter('firstHalf'); setIsFilterSheetOpen(false); }}>1st-15th</button>
                                    <button className={dateFilter === 'secondHalf' ? 'active' : ''} onClick={() => { setDateFilter('secondHalf'); setIsFilterSheetOpen(false); }}>16th-End</button>
                                    <button className={dateFilter === 'today' ? 'active' : ''} onClick={() => { setDateFilter('today'); setIsFilterSheetOpen(false); }}>Today</button>
                                    <button className={dateFilter === 'month' ? 'active' : ''} onClick={() => { setDateFilter('month'); setIsFilterSheetOpen(false); }}>This Month</button>
                                    <button className={dateFilter === 'custom' ? 'active' : ''} onClick={() => setDateFilter('custom')}>Custom</button>
                                </div>
                                {dateFilter === 'custom' && (
                                    <div className="custom-date-inputs">
                                        <input type="date" value={customDate.start} onChange={e => setCustomDate({...customDate, start: e.target.value})} className="form-input"/>
                                        <input type="date" value={customDate.end} min={customDate.start} onChange={e => setCustomDate({...customDate, end: e.target.value})} className="form-input"/>
                                    </div>
                                )}
                            </div>
                            <div className="filter-group button-group">
                                <button onClick={() => setIsFilterSheetOpen(false)} className="action-button">Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {!summary && !isGenerating && ( <div className="placeholder-text">No data found for these filters.</div> )}
            
            {summary && (
                <div className="report-data-container" style={{ opacity: isGenerating ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    <div className="card summary-section">
                        <h2 className="section-title" title="Total hours..." style={{ cursor: 'help' }} >
                            <Info size={18} />
                            <span>Total Hours - {totalWorkHours}</span>
                            {isGenerating && <Loader2 className="spin" size={16} style={{marginLeft: 'auto'}}/>}
                        </h2>
                        <div className="summary-grid">
                            {Object.keys(summary).length > 0 && Object.values(summary).map((s) => (
                                <div key={s.employee_name} className="summary-item">
                                    <span className="summary-employee-name">{s.employee_name}</span>
                                    <div className="summary-hours">
                                        <Clock size={14} /> 
                                        <span>{s.total_hours}</span>
                                        {s.overtime_hours && (
                                            <span style={{ 
                                                marginLeft: '8px', 
                                                color: '#ef4444', 
                                                fontWeight: '600', 
                                                fontSize: '0.9em' 
                                            }}>
                                                OT: {s.overtime_hours}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {timeEntries.length > 0 && (
                        <div className="card report-container">
                            <h2 className="section-title">Detailed Time Records</h2>
                            <div className="detailed-list mobile-only">
                                {timeEntries.map(entry => (
                                    <React.Fragment key={entry.id}>
                                        <div className="detailed-list-item">
                                            <div className="list-item-main-content" onClick={() => handleToggleExpand(entry.id)}>
                                                <div className="list-item-row">
                                                    <span className="list-item-employee">
                                                        {entry.employee_name}
                                                        <span className="employee-property-acronym">
                                                            {generateAcronym(entry.property_name)}
                                                        </span>
                                                    </span>
                                                    <span className="list-item-duration">{entry.duration || '-'}</span>
                                                </div>
                                                <div className="list-item-row sub-details">
                                                    <span>{entry.date}</span>
                                                    <span>
                                                        {new Date(entry.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                        {' → '}
                                                        {entry.end_time ? new Date(entry.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'Now'}
                                                    </span>
                                                    <span className={`status-badge ${entry.status === 'In Progress' ? 'in-progress' : 'completed'}`}>{entry.status}</span>
                                                </div>
                                            </div>
                                            {canEdit && (
                                                <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleOpenEditModal(entry); }}>
                                                    <Edit size={18} />
                                                </button>
                                            )}
                                        </div>
                                        {expandedEntryId === entry.id && (
                                            <div className="edit-history-content mobile-history">
                                                <h4>Edit History</h4>
                                                <p><strong>Last Edit Type:</strong> {entry.last_edit_type || 'SYSTEM_GENERATED'}</p>
                                                <p><strong>Notes:</strong> {entry.notes || 'N/A'}</p>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            <table className="data-table desktop-only">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th>Date</th>
                                        <th>Employee</th>
                                        <th>Property</th>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                        <th>Duration</th>
                                        <th>Status</th>
                                        {canEdit && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeEntries.map(entry => (
                                        <React.Fragment key={entry.id}>
                                            <tr>
                                                <td>
                                                    <button onClick={() => handleToggleExpand(entry.id)} className="icon-button">
                                                        {expandedEntryId === entry.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </td>
                                                <td data-label="Date"><span>{entry.date}</span></td>
                                                <td data-label="Date"><span>{entry.employee_name}</span></td>
                                                <td data-label="Property"><span>{entry.property_name}</span></td>
                                                <td data-label="Start Time"><span>{new Date(entry.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span></td>
                                                <td data-label="End Time"><span>{entry.end_time ? new Date(entry.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-'}</span></td>
                                                <td data-label="Duration"><span>{entry.duration || '-'}</span></td>
                                                <td data-label="Status"><span className={`status-badge ${entry.status === 'In Progress' ? 'in-progress' : 'completed'}`}>{entry.status}</span></td>
                                                {canEdit && (<td data-label="Actions"><button onClick={() => handleOpenEditModal(entry)} className="icon-button"><Edit size={16} /></button></td>)}
                                            </tr>
                                            {expandedEntryId === entry.id && (
                                                <tr className="edit-history-row"><td colSpan="9"><div className="edit-history-content"><h4>Edit History</h4><p><strong>Last Edit Type:</strong> {entry.last_edit_type || 'SYSTEM_GENERATED'}</p><p><strong>Notes:</strong> {entry.notes || 'N/A'}</p></div></td></tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            
            {isEditModalOpen && editingEntry && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h2>Edit Entry for {editingEntry.employee_name}</h2>
                        
                        {/* --- Clean Text Display for Start Time --- */}
                        <p className="modal-subtitle" style={{ marginBottom: '20px', color: '#64748b', fontSize: '0.95rem' }}>
                            <strong>Date:</strong> {new Date(editingEntry.start_time).toLocaleDateString()} 
                            <span style={{ margin: '0 10px', color: '#cbd5e1' }}>|</span> 
                            <strong>Start Time:</strong> {new Date(editingEntry.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </p>

                        {/* --- END TIME (Date + Time Selection) --- */}
                        <div className="form-group">
                            <label className="form-label">End Date & Time</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {/* Date Input */}
                                <input 
                                    type="date" 
                                    className="form-input" 
                                    // Logic: Prevent picking a date BEFORE the start date
                                    min={getInputValue(editingEntry.start_time, 'date')}
                                    
                                    // Default to start date if currently null
                                    value={getInputValue(editingEntry.end_time || editingEntry.start_time, 'date')} 
                                    onChange={e => handleDateTimeChange('end_time', 'date', e.target.value)} 
                                />
                                
                                {/* Time Input */}
                                <input 
                                    type="time" 
                                    className="form-input" 
                                    style={{ 
                                        // Turn border red if End <= Start (and End is actually set)
                                        borderColor: (editingEntry.end_time && new Date(editingEntry.end_time) <= new Date(editingEntry.start_time)) ? '#ef4444' : '#cbd5e0' 
                                    }}
                                    // Logic: If dates match, restrict time to be AFTER start time. Else, allow any time.
                                    min={
                                        getInputValue(editingEntry.end_time || editingEntry.start_time, 'date') === getInputValue(editingEntry.start_time, 'date') 
                                        ? getInputValue(editingEntry.start_time, 'time') 
                                        : null
                                    }
                                    
                                    value={getInputValue(editingEntry.end_time, 'time')} 
                                    onChange={e => handleDateTimeChange('end_time', 'time', e.target.value)} 
                                />
                            </div>
                            <small style={{ color: '#64748b', display: 'block', marginTop: '5px' }}>
                                Tip: If the shift went past midnight, change the <b>End Date</b> to the next day.
                            </small>
                        </div>

                        <div className="form-group">
                            <label>Notes (Required)</label>
                            <textarea 
                                className="form-input" 
                                value={editingEntry.notes || ''} 
                                onChange={e => setEditingEntry({...editingEntry, notes: e.target.value})} 
                                rows="3"
                                placeholder="Reason for adjustment..."
                            ></textarea>
                        </div>

                        <div className="modal-actions">
                            <button onClick={handleCloseEditModal} className="button button-secondary">Cancel</button>
                            <button 
                                onClick={handleSaveEntry} 
                                className="button action-button"
                                // Disable button if form is invalid
                                disabled={!isEditFormValid}
                                // Show tooltip explaining why it is disabled
                                title={!isEditFormValid ? "Enter valid End Time (after Start Time) and Notes to save." : ""}
                                style={{ opacity: !isEditFormValid ? 0.5 : 1, cursor: !isEditFormValid ? 'not-allowed' : 'pointer' }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SummaryReportModal
                isOpen={isSummaryModalOpen}
                onClose={() => setIsSummaryModalOpen(false)}
                summary={summary}
                timeEntries={timeEntries}
                properties={properties}
                selectedProperty={selectedProperty}
                dateFilter={dateFilter}
                customDate={customDate}
                payrollData={payrollData}
                user={user}
            />

            {isPayrollModalOpen && (
                <SplitPayrollModal
                    onClose={() => {
                        setIsPayrollModalOpen(false);
                        fetchReportData();
                    }}
                    summaryData={summary}
                    propertyId={selectedProperty}
                    startDate={reportDateRange.start}
                    endDate={reportDateRange.end}
                    periodType={periodType}
                />
            )}
        </div>
    );
};

export default ReportScreen;